/* eslint-disable react/no-unescaped-entities */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/lib/api';
import { getSessionId, captureUtm } from '@/lib/track';

// Run fn when the browser is idle (after the critical render). Falls back to a
// short timeout where requestIdleCallback is unavailable (older Safari).
const defer = (fn) => {
  if (typeof window !== 'undefined' && typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout: 3000 });
  } else {
    setTimeout(fn, 1200);
  }
};

// Google tag (gtag.js): works for Google Ads (AW-…) and GA4 (G-…) ids.
function injectGoogle(googleTagId) {
  if (!googleTagId || window.__ec_gt_loaded) return;
  window.__ec_gt_loaded = true;
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', googleTagId);
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(googleTagId)}`;
  document.head.appendChild(s);
}

// Fire a Google event. Purchase also fires the Ads "conversion" when a label is set.
function fireGoogle(event, { googleTagId, googleLabel, amount = 0, currency = 'EUR', transactionId = '' }) {
  if (!googleTagId || !window.gtag) return;
  const g = { page_view_campaign: 'view_item', page_view_checkout: 'begin_checkout', view: 'view_item', checkout: 'begin_checkout', purchase: 'purchase' }[event];
  if (!g) return;
  const value = Number(amount) || 0;
  try {
    if (g === 'purchase') {
      window.gtag('event', 'purchase', { value, currency, transaction_id: transactionId || undefined });
      if (googleLabel) window.gtag('event', 'conversion', { send_to: `${googleTagId}/${googleLabel}`, value, currency, transaction_id: transactionId || undefined });
    } else {
      window.gtag('event', g, value ? { value, currency } : undefined);
    }
  } catch {}
}

// Inject Meta + TikTok + Google pixel snippets once per page. The inline snippets define
// the fbq/ttq queue stubs AND kick off the heavy external script downloads, so
// running this at idle keeps tracking intact (queued calls flush once loaded).
function injectPixels(metaPixelId, tiktokPixelId, googleTagId) {
  injectGoogle(googleTagId);
  if (metaPixelId && !window.__ec_fb_loaded) {
    window.__ec_fb_loaded = true;
    const s = document.createElement('script');
    s.async = true;
    s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${metaPixelId}');fbq('track','PageView');`;
    document.head.appendChild(s);
  }
  if (tiktokPixelId && !window.__ec_tt_loaded) {
    window.__ec_tt_loaded = true;
    const s = document.createElement('script');
    s.async = true;
    s.text = `!function (w, d, t) {w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};ttq.load('${tiktokPixelId}');ttq.page();}(window, document, 'ttq');`;
    document.head.appendChild(s);
  }
}

// Standalone pixel helpers reusable outside the store context (e.g. payment
// links which have no store_id but still carry Meta/TikTok pixel ids).
export function injectLinkPixels(metaPixelId, tiktokPixelId, googleTagId) {
  defer(() => injectPixels(metaPixelId, tiktokPixelId, googleTagId));
}

// Fire a standard pixel event on demand. event: 'view' | 'checkout' | 'purchase'.
export function fireLinkPixel(event, { metaPixelId, tiktokPixelId, googleTagId, googleLabel, amount = 0, currency = 'EUR', transactionId = '' } = {}) {
  defer(() => {
    fireGoogle(event, { googleTagId, googleLabel, amount, currency, transactionId });
    const fbMap = { view: 'ViewContent', checkout: 'InitiateCheckout', purchase: 'Purchase' };
    const ttMap = { view: 'ViewContent', checkout: 'InitiateCheckout', purchase: 'CompletePayment' };
    const payload = event === 'purchase' ? { value: Number(amount) || 0, currency } : undefined;
    if (metaPixelId && window.fbq && fbMap[event]) { try { window.fbq('track', fbMap[event], payload); } catch {} }
    if (tiktokPixelId && window.ttq && ttMap[event]) { try { window.ttq.track(ttMap[event], payload); } catch {} }
  });
}

/**
 * Inject Meta + TikTok pixels for a given store, AND record our internal events
 * for the per-slug dashboard. Renders nothing.
 *
 * Props:
 *   storeId       — required, the internal store id (for backend tracking)
 *   metaPixelId   — optional, Facebook/Meta Pixel id
 *   tiktokPixelId — optional, TikTok Pixel id
 *   googleTagId   — optional, Google tag (AW-… Google Ads / G-… GA4)
 *   googleLabel   — optional, Google Ads conversion label (Purchase conversion)
 *   slug          — optional, the campaign slug (only meaningful on /c/:slug)
 *   event         — one of 'page_view_campaign' | 'page_view_checkout' | 'purchase'
 *   amount        — optional, for purchase event
 *   paymentIntentId — optional, for purchase event
 */
export default function PixelTracker({
  storeId, metaPixelId, tiktokPixelId, googleTagId = '', googleLabel = '', slug = '', event,
  amount = 0, paymentIntentId = '', currency = 'EUR',
}) {
  const firedRef = useRef(false);

  // Inject pixel <script> tags once per page (idempotent). Deferred to browser
  // idle time so the heavy fbevents.js / TikTok events.js downloads don't spike
  // memory during the critical checkout render — a known cause of mobile Safari
  // tab reloads on heavy payment pages.
  useEffect(() => {
    if (!metaPixelId && !tiktokPixelId && !googleTagId) return;
    defer(() => injectPixels(metaPixelId, tiktokPixelId, googleTagId));
  }, [metaPixelId, tiktokPixelId, googleTagId]);

  // Fire the standard event (once per mount).
  useEffect(() => {
    if (firedRef.current || !storeId || !event) return;
    firedRef.current = true;
    const sessionId = getSessionId();
    const utm = captureUtm();

    // 1. Send to our backend (always) — UTMs included so the dashboard can
    // attribute sessions to campaigns.
    axios.post(`${API}/public/events`, {
      type: event,
      store_id: storeId,
      session_id: sessionId,
      slug,
      payment_intent_id: paymentIntentId,
      amount: Number(amount) || 0,
      currency: 'eur',
      utm_source: utm.utm_source || '',
      utm_medium: utm.utm_medium || '',
      utm_campaign: utm.utm_campaign || '',
      utm_term: utm.utm_term || '',
      utm_content: utm.utm_content || '',
    }).catch(() => {});

    // 2 & 3. Mirror to Meta + TikTok pixels — deferred so the fbq/ttq queue
    // stubs (injected at idle) exist before we call track(). Queued calls flush
    // automatically once the external scripts finish loading.
    defer(() => {
      fireGoogle(event, { googleTagId, googleLabel, amount, currency: (currency || 'EUR').toUpperCase(), transactionId: paymentIntentId });
      if (metaPixelId && window.fbq) {
        const fbEvent = {
          page_view_campaign: 'PageView',
          page_view_checkout: 'InitiateCheckout',
          purchase: 'Purchase',
        }[event];
        if (fbEvent) {
          const payload = event === 'purchase'
            ? { value: Number(amount) || 0, currency: 'EUR' }
            : undefined;
          try { window.fbq('track', fbEvent, payload); } catch {}
        }
      }
      if (tiktokPixelId && window.ttq) {
        const ttEvent = {
          page_view_campaign: 'ViewContent',
          page_view_checkout: 'InitiateCheckout',
          purchase: 'CompletePayment',
        }[event];
        if (ttEvent) {
          const payload = event === 'purchase'
            ? { value: Number(amount) || 0, currency: 'EUR' }
            : undefined;
          try { window.ttq.track(ttEvent, payload); } catch {}
        }
      }
    });
  }, [storeId, event, amount, slug, paymentIntentId, metaPixelId, tiktokPixelId, googleTagId, googleLabel, currency]);

  return null;
}
